/*
 * O que os resultados de voo LEEM de uma oferta, declarado pela FORMA e não
 * pelo wire do chat (#4997): o kit não importa o genui-kit nem o domain. Só os
 * campos que alguém aqui lê entram. O `ChatUiFlightOffer` do genui-kit e os
 * tipos do domain cabem nestes tipos — o `flight_offer_source.test.ts` do
 * genui-kit prova isso no typecheck, então uma mudança no wire quebra a
 * compilação ali, não a tela.
 */

export interface FlightMoneySource {
  amount: number;
  currency: string;
}

export interface FlightSegmentSource {
  from: string;
  to: string;
  /** Data-hora em texto: o provider não garante ISO, só o `THH:MM`. */
  departAt: string;
  arriveAt: string;
  carrier: string;
  flightNumber: string;
  /** Nome de exibição da companhia; pode chegar vazio (cai no código). */
  carrierName?: string;
  durationMinutes?: number;
  /** Duração pronta do produtor ("2h 05m"); ausente quando os horários não parseiam. */
  durationLabel?: string;
  /** As escalas do trecho, só nas ofertas curadas: a linha desenha um nó por escala. */
  connections?: readonly FlightConnectionSource[];
}

/** Uma escala do trecho: o aeroporto de conexão e a espera até o próximo voo. */
export interface FlightConnectionSource {
  iata: string;
  /** Espera formatada ("1h 25m"); ausente quando o gateway não a trouxe. */
  layoverLabel?: string;
}

export interface FlightOfferSource {
  offerId: string;
  /** A IDA (ou o trecho único). */
  segments: readonly FlightSegmentSource[];
  price: FlightMoneySource;
  /** Paradas do provider; ausente ⇒ `segments.length - 1`. */
  stops?: number;
  /** Os destaques do fio; a lista recomendada lê `best`/`cheap`/`fast`. O
   *  `label` é o rótulo do produtor, só para um eixo que a tela não desenha. */
  badges?: readonly { kind: string; label?: string }[];
  /** Veredito de política: `in` passa no filtro "dentro da política". */
  policy?: {
    status: string;
    ceiling?: FlightMoneySource;
    /** As regras que a gaveta "Detalhes da Política" lista. */
    rules?: readonly FlightPolicyRuleSource[];
  };
  /** A VOLTA de um combo ida+volta. */
  returnLeg?: { segments: readonly FlightSegmentSource[]; stops?: number };
  /** A nota do agente: por que ESTE voo está na lista. */
  why?: string;
}

/** Uma regra da política aplicada, com o status dela nesta oferta. */
export interface FlightPolicyRuleSource {
  /** A chave do produtor; o host a traduz, e a fora da tabela cai no `title`. */
  key: string;
  /** O título pt-BR do produtor, o fallback. */
  title: string;
  status: 'ok' | 'violated' | 'na';
  /** O texto rico da regra, quando o produtor o mandou. */
  text?: string;
  /** A janela de antecedência, que a tela escreve no idioma de quem lê. */
  params?: { days: number; scope: 'domestic' | 'international' };
}

/** O nome de exibição da companhia de um trecho: o nome com conteúdo, senão o
 *  código. A mesma regra do `carrierDisplayName` do genui-kit, que mora no
 *  schema da raiz dele e por isso não pode vir do kit. */
export function carrierNameOf(seg: Pick<FlightSegmentSource, 'carrier' | 'carrierName'>): string {
  return seg.carrierName?.trim() || seg.carrier.trim();
}
